import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Snackbar } from '@gapartners/ui';
import { api } from '@/store/api';
import { CloseCaseLoop } from '@/types/case';
import type { CustomerCase } from '@/types/customer';
import { formatCallerDisplayName } from '@/utils/customer';
import type { DisplayCall } from './CallHistoryCardTypes';
import { CaseFormDialog, type CaseFormFields } from './CaseFormDialog';
import {
  DEFAULT_CASE_REPORTING_METHOD,
  attachContactIdToDescription,
} from './caseFormConstants';
import {
  createEmptyCaseFormFields,
  useCaseFormEditor,
} from './useCaseFormEditor';

const MISSED_CALL_CATEGORY_CODE = 'HRCC-Inbox';
const MISSED_CALL_CASE_TYPE_NAME = 'VM-Voicemail';

export interface CallHistoryCreateCaseDialogProps {
  selectedCall: DisplayCall | null;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Pre-fill HRCC-Inbox / VM-Voicemail defaults (missed calls). */
  useMissedCallDefaults?: boolean;
}

export function CallHistoryCreateCaseDialog({
  selectedCall,
  open,
  onClose,
  onCreated,
  useMissedCallDefaults = false,
}: CallHistoryCreateCaseDialogProps) {
  const { t } = useTranslation();
  const { useCreateCaseMutation, useLazyGetCaseTypesByCategoryQuery } = api;
  const [createCase, { isLoading: isCreatingCase }] = useCreateCaseMutation();
  const [fetchCaseTypesByCategory] = useLazyGetCaseTypesByCategoryQuery();

  const unknownCallerLabel = t('components.contactCenter.unknownCaller');
  const workspaceId = selectedCall?.workspaceId
    ? Number(selectedCall.workspaceId)
    : null;

  const caseEditor = useCaseFormEditor({
    caseInfo: null,
    contactId: selectedCall?.contactId ?? null,
    syncWhen: false,
    workspaceId,
  });

  const [formFields, setFormFields] = useState<CaseFormFields>(
    createEmptyCaseFormFields()
  );
  const [filteredCaseTypeOptions, setFilteredCaseTypeOptions] = useState<
    Array<{ value: number; label: string }>
  >([]);
  const defaultsAppliedRef = useRef(false);
  const wasOpenRef = useRef(false);
  const [relatedCase, setRelatedCase] = useState<CustomerCase | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const resolveNewStatus = useCallback((): string => {
    const match = caseEditor.statusOptions.find(
      option =>
        option.value.toLowerCase() === 'new' ||
        option.label.toLowerCase() === 'new'
    );
    return match?.value ?? 'New';
  }, [caseEditor.statusOptions]);

  const buildInitialFormFields = useCallback(
    (call: DisplayCall): CaseFormFields => {
      const callerName = formatCallerDisplayName(
        call.clientDetails?.name,
        unknownCallerLabel
      );
      const base = {
        ...createEmptyCaseFormFields(resolveNewStatus()),
        closeCaseLoop: CloseCaseLoop.PHONE,
        reportingMethod: DEFAULT_CASE_REPORTING_METHOD,
        phoneNumber:
          call.clientDetails?.phoneNumber?.trim() || call.phoneNumber || '',
        reportedByName: callerName,
        // Attach the interaction's Contact ID into the case description on
        // create only. Edit mode loads the stored description as-is, so the
        // ID is never duplicated.
        description: attachContactIdToDescription(
          '',
          call.contactId,
          t('components.contactCenter.contactId')
        ),
      };
      if (useMissedCallDefaults) {
        return {
          ...base,
          subject: t('components.contactCenter.missedCallDefaultSubject'),
        };
      }
      return base;
    },
    [resolveNewStatus, t, unknownCallerLabel, useMissedCallDefaults]
  );

  useEffect(() => {
    if (!open || !selectedCall) {
      wasOpenRef.current = false;
      return;
    }
    // Rebuild the form only when the dialog opens — not on every identity
    // change of buildInitialFormFields (e.g. status options resolving late),
    // which would wipe the applied defaults and user input.
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    defaultsAppliedRef.current = false;
    setRelatedCase(null);
    setFormFields(buildInitialFormFields(selectedCall));
    setFilteredCaseTypeOptions([]);
  }, [open, selectedCall, buildInitialFormFields]);

  const applyMissedCallCategoryDefaults = useCallback(async () => {
    if (!useMissedCallDefaults) return;

    const category = caseEditor.categories.find(
      cat => cat.code === MISSED_CALL_CATEGORY_CODE
    );
    if (!category) return;

    let caseTypeId: number | null = null;
    let caseType = '';
    let categoryPriority = formFields.categoryPriority;

    try {
      const result = await fetchCaseTypesByCategory(category.code).unwrap();
      const options = (result.caseTypes ?? []).map(ct => ({
        value: ct.id,
        label: ct.name,
      }));
      setFilteredCaseTypeOptions(options);

      const vmType = result.caseTypes?.find(
        ct => ct.name === MISSED_CALL_CASE_TYPE_NAME
      );
      if (vmType) {
        caseTypeId = vmType.id;
        caseType = vmType.name;
        categoryPriority = vmType.priority ?? categoryPriority;
      }
    } catch {
      setFilteredCaseTypeOptions([]);
    }

    setFormFields(prev => ({
      ...prev,
      categoryCode: category.code,
      categoryName: category.name,
      category: category.name,
      categoryPriority: categoryPriority || prev.categoryPriority,
      caseTypeId,
      caseType,
    }));
  }, [
    useMissedCallDefaults,
    caseEditor.categories,
    fetchCaseTypesByCategory,
    formFields.categoryPriority,
  ]);

  useEffect(() => {
    if (!open || !useMissedCallDefaults) {
      defaultsAppliedRef.current = false;
      return;
    }
    if (defaultsAppliedRef.current) return;
    if (caseEditor.categories.length === 0) return;

    defaultsAppliedRef.current = true;
    void applyMissedCallCategoryDefaults();
  }, [
    open,
    useMissedCallDefaults,
    caseEditor.categories,
    applyMissedCallCategoryDefaults,
  ]);

  const prevCategoryCodeRef = useRef(formFields.categoryCode);
  useEffect(() => {
    if (!open) return;
    if (formFields.categoryCode === prevCategoryCodeRef.current) return;
    prevCategoryCodeRef.current = formFields.categoryCode;

    if (!formFields.categoryCode) {
      setFilteredCaseTypeOptions([]);
      return;
    }

    void (async () => {
      try {
        const result = await fetchCaseTypesByCategory(
          formFields.categoryCode
        ).unwrap();
        setFilteredCaseTypeOptions(
          (result.caseTypes ?? []).map(ct => ({
            value: ct.id,
            label: ct.name,
          }))
        );
      } catch {
        setFilteredCaseTypeOptions([]);
      }
    })();
  }, [open, formFields.categoryCode, fetchCaseTypesByCategory]);

  const handleContactChanged = useCallback(
    (contact: { phone?: string; email?: string }) => {
      setFormFields(prev => ({
        ...prev,
        phoneNumber: contact.phone ?? prev.phoneNumber,
        email: contact.email ?? prev.email,
      }));
    },
    []
  );

  const handleCreateCase = async () => {
    if (!selectedCall || !workspaceId) {
      setToast({
        message: t('components.contactCenter.workspaceIdRequired'),
        type: 'error',
      });
      return;
    }

    if (!formFields.categoryCode || !formFields.categoryName.trim()) {
      setToast({
        message: t('components.contactCenter.caseCategoryRequired'),
        type: 'error',
      });
      return;
    }

    if (formFields.caseTypeId == null || formFields.caseTypeId <= 0) {
      setToast({
        message: t('components.contactCenter.caseTypeRequired'),
        type: 'error',
      });
      return;
    }

    try {
      const response = await createCase({
        contactId: selectedCall.contactId,
        workspaceId,
        status: formFields.status,
        categoryCode: formFields.categoryCode,
        caseTypeId: formFields.caseTypeId,
        categoryPriority: formFields.categoryPriority || '',
        callerName:
          formFields.reportedByName ||
          formatCallerDisplayName(
            selectedCall.clientDetails?.name,
            unknownCallerLabel
          ),
        reportedByName: formFields.reportedByName || undefined,
        subject: formFields.subject,
        description: formFields.description,
        callType: selectedCall.type === 'chat' ? 'chat' : 'call',
        subCaseType: formFields.subCaseType,
        resolution: formFields.resolution,
        phoneNumber:
          formFields.phoneNumber ||
          selectedCall.clientDetails?.phoneNumber ||
          selectedCall.phoneNumber ||
          '',
        email: formFields.email,
        closeCaseLoop:
          selectedCall.type === 'chat'
            ? CloseCaseLoop.CHAT
            : CloseCaseLoop.PHONE,
        origination: formFields.origination,
        employeeId: formFields.employeeId ?? undefined,
        reportedByEmployeeId: formFields.reportedByEmployeeId ?? 0,
        assignedTo: formFields.assignedTo ?? undefined,
        clientContactId: formFields.clientContactId || undefined,
        resolutionType: formFields.resolutionType || undefined,
        reportingMethod: formFields.reportingMethod || undefined,
        internalNotes: formFields.notes || undefined,
        relatedCaseId:
          relatedCase?.id != null ? Number(relatedCase.id) : undefined,
      }).unwrap();

      onClose();
      onCreated();
      setToast(
        response.relatedCaseLinked === false
          ? {
              message: t(
                'components.contactCenter.caseCreatedRelatedCaseLinkFailed'
              ),
              type: 'warning',
            }
          : {
              message: t('components.contactCenter.caseCreatedSuccess'),
              type: 'success',
            }
      );
    } catch {
      setToast({
        message: t('components.contactCenter.caseCreationFailed'),
        type: 'error',
      });
    }
  };

  const callerName = formatCallerDisplayName(
    selectedCall?.clientDetails?.name,
    unknownCallerLabel
  );

  return (
    <>
      <CaseFormDialog
        mode="create"
        open={open}
        onClose={onClose}
        onSubmit={handleCreateCase}
        isLoading={isCreatingCase}
        formFields={formFields}
        onFormFieldChange={(field, value) => {
          setFormFields(prev => ({
            ...prev,
            [field]: value,
          }));
        }}
        statusOptions={caseEditor.statusOptions}
        subCaseTypeTree={caseEditor.subCaseTypeTree}
        originationOptions={caseEditor.originationOptions}
        priorityOptions={caseEditor.priorityOptions}
        caseTypeOptions={
          filteredCaseTypeOptions.length > 0
            ? filteredCaseTypeOptions
            : caseEditor.caseTypeOptions
        }
        categories={caseEditor.categories}
        contactId={selectedCall?.contactId}
        closeCaseLoopSource="activeCall"
        activeCallCloseCaseLoop={
          selectedCall?.type === 'chat'
            ? CloseCaseLoop.CHAT
            : CloseCaseLoop.PHONE
        }
        employeeOptions={caseEditor.employeeOptions}
        internalUserOptions={caseEditor.internalUserOptions}
        resolutionTypeOptions={caseEditor.resolutionTypeOptions}
        clientContactOptions={caseEditor.clientContactOptions}
        callerName={callerName}
        onContactChanged={handleContactChanged}
        relatedCaseWorkspaceId={selectedCall?.workspaceId ?? null}
        relatedCase={relatedCase}
        onRelatedCaseChange={setRelatedCase}
      />

      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </>
  );
}
